import React, { useState, useEffect, useCallback } from 'react';
import './ActivitiesTab.css';

const todayISO = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y.slice(2)}`;
};

function ActivitiesTab({ svc, onChanged }) {
  const [activities, setActivities] = useState([]);
  const [today, setToday] = useState(todayISO());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [due, setDue] = useState(todayISO());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await svc.getActivities();
      if (d.error) throw new Error(d.error);
      setActivities(d.activities || []);
      if (d.today) setToday(d.today);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [svc]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => { await load(); if (onChanged) onChanged(); };

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true); setError(null);
    try {
      const d = await svc.createActivity(name.trim(), due);
      if (d.error) throw new Error(d.error);
      setName('');
      await refresh();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const toggle = async (a) => {
    try { await svc.toggleActivity(a.id); await refresh(); }
    catch (e) { setError(e.message); }
  };

  const remove = async (a) => {
    try { await svc.deleteActivity(a.id); await refresh(); }
    catch (e) { setError(e.message); }
  };

  const pending = activities.filter((a) => !a.done);
  const done = activities.filter((a) => a.done);
  const groups = [
    { key: 'atrasadas', label: 'Atrasadas', items: pending.filter((a) => a.due_date < today) },
    { key: 'hoje', label: 'Hoje', items: pending.filter((a) => a.due_date === today) },
    { key: 'proximas', label: 'Próximas', items: pending.filter((a) => a.due_date > today) },
    { key: 'concluidas', label: 'Concluídas', items: done },
  ];

  const Item = ({ a, overdue }) => (
    <div className={`ac-item ${a.done ? 'done' : ''}`}>
      <button className={`ac-check ${a.done ? 'on' : ''}`} onClick={() => toggle(a)} aria-label="Concluir">
        {a.done ? '✓' : ''}
      </button>
      <span className="ac-name">{a.name}</span>
      <span className={`ac-date ${overdue ? 'late' : ''}`}>{fmtDate(a.due_date)}</span>
      <button className="ac-del" onClick={() => remove(a)} aria-label="Excluir">✕</button>
    </div>
  );

  return (
    <div className="ac-view">
      <div className="ac-form">
        <input
          className="ac-input ac-input-name"
          placeholder="Nova atividade…"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
        />
        <input
          className="ac-input ac-input-date"
          type="date"
          value={due}
          onChange={(e) => setDue(e.target.value)}
        />
        <button className="ac-add" onClick={add} disabled={busy || !name.trim()}>
          {busy ? '…' : 'Adicionar'}
        </button>
      </div>

      {error && <p className="ac-msg ac-err">{error}</p>}
      {loading && activities.length === 0 && <p className="ac-msg">Carregando…</p>}
      {!loading && activities.length === 0 && (
        <p className="ac-msg">Nenhuma atividade. Cadastre a primeira acima.</p>
      )}

      {groups.map((g) => g.items.length > 0 && (
        <div className="ac-group" key={g.key}>
          <div className={`ac-group-title ${g.key}`}>{g.label} <span className="ac-count">{g.items.length}</span></div>
          {g.items.map((a) => <Item key={a.id} a={a} overdue={g.key === 'atrasadas'} />)}
        </div>
      ))}
      <div className="ac-spacer" />
    </div>
  );
}

export default ActivitiesTab;
